<script setup lang="ts">
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { computed, ref, watch } from 'vue'
  import {
    AiAugmentedByMapperKeysEnum as DatamapperKey,
    type CustomerCase as Case
  } from '@/types/generated'
  import InputField from '@/components/admin/form/InputField.vue'
  import { CardContent, Card, CardTitle } from '@/components/ui/card'
  import { debounce } from '@/lib/timing'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import Button from '@/components/ui/button/Button.vue'
  import ScrollArea from '@/components/ui/scroll-area/ScrollArea.vue'

  // Translations
  const { t } = useTranslation({
    en: {
      dates: 'Dates',
      repairCosts: 'Repair Costs',
      values: 'Values',
      tax: 'Tax',
      results: 'Results',
      accidentDate: 'Accident Date',
      expertDate: 'Expert Report Date',
      netRepairCosts: 'Net Repair Costs',
      grossRepairCosts: 'Gross Repair Costs',
      repairDuration: 'Repair Duration',
      valueLoss: 'Value Loss',
      netReplacementValue: 'Net Replacement Value',
      grossReplacementValue: 'Gross Replacement Value',
      grossResidualValue: 'Gross Residual Value',
      netGrossReplacementCosts: 'Net-Gross Replacement Costs',
      grossGrossReplacementCosts: 'Gross-Gross Replacement Costs',
      differentialTax: 'Differential Tax',
      regularTax: 'Regular Tax',
      damageAssessmentTime: 'Damage Assessment Time',
      considerationPeriod: 'Consideration Period',
      dayOrDays: 'Day(s)',
      days: 'days',
      compensationForLossOfUse: 'Compensation for Loss of Use'
    },
    de: {
      dates: 'Termine',
      repairCosts: 'Reparaturkosten',
      values: 'Werte',
      tax: 'Steuer',
      results: 'Ergebnisse',
      accidentDate: 'Unfalldatum',
      expertDate: 'Datum Gutachten',
      netRepairCosts: 'Nettoreparaturkosten',
      grossRepairCosts: 'Bruttoreparaturkosten',
      repairDuration: 'Reparaturdauer',
      valueLoss: 'Wertminderung',
      netReplacementValue: 'Nettowiederbeschaffungswert',
      grossReplacementValue: 'Bruttowiederbeschaffungswert',
      grossResidualValue: 'Bruttorestwert',
      netGrossReplacementCosts: 'Netto-Brutto-Wiederbeschaffungsaufwand',
      grossGrossReplacementCosts: 'Brutto-Brutto-Wiederbeschaffungsaufwand',
      differentialTax: 'Differenzsteuer',
      regularTax: 'Regelsteuer',
      damageAssessmentTime: 'Schadensermittlungszeit',
      considerationPeriod: 'Überlegungsfrist',
      dayOrDays: 'Tag(e)',
      days: 'Tage',
      compensationForLossOfUse: 'Nutzungsausfallentschädigung'
    }
  })

  const caseStore = useCaseStore()
  const caseRecord = computed<Case>(() => caseStore.caseRecord as Case)
  const isLoading = computed(() => !caseRecord.value?.data)

  // Input fields with store bindings
  const accidentDate = ref<string>('')
  const expertDate = ref<string>('')
  const repairDuration = ref<number>(0)

  // Numeric input fields
  const repairCostsWithVat = ref<number>(0)
  const repairCostsWithoutVat = computed(() => {
    return repairCostsWithVat.value / 1.19
  })

  // This section was intentionally left empty as the netReplacementValue is already defined below

  const merantileValueLoss = ref<number>(0)
  const grossReplacementValue = ref<number>(0)
  const grossResidualValue = ref<number>(0)

  // Tax options
  const isDifferenzsteuer = ref<boolean>(false)
  const isRegelsteuer = ref<boolean>(false)

  // Computed values
  const netReplacementValue = computed(() => {
    if (isDifferenzsteuer.value) {
      return grossReplacementValue.value / 1.025
    } else if (isRegelsteuer.value) {
      return grossReplacementValue.value / 1.19
    }
    return grossReplacementValue.value
  })

  // Calculate replacement costs
  const netGrossReplacementCosts = computed(() => {
    return netReplacementValue.value - grossResidualValue.value
  })

  const grossGrossReplacementCosts = computed(() => {
    return grossReplacementValue.value - grossResidualValue.value
  })

  // Helper function to convert dd.mm.yyyy to yyyy-MM-dd format for HTML date inputs
  const germanDateToIsoDate = (germanDate: string): string => {
    if (!germanDate) return ''

    // Split the date string by dots
    const parts = germanDate.split('.')
    if (parts.length !== 3) return ''

    // Rearrange to yyyy-MM-dd format
    return `${parts[2]}-${parts[1]}-${parts[0]}`
  }

  // Helper function to convert yyyy-MM-dd to dd.mm.yyyy format for backend
  const isoDateToGermanDate = (isoDate: string): string => {
    if (!isoDate) return ''

    // Split the date string by hyphens
    const parts = isoDate.split('-')
    if (parts.length !== 3) return ''

    // Rearrange to dd.mm.yyyy format
    return `${parts[2]}.${parts[1]}.${parts[0]}`
  }

  // Load initial data
  const loadInitialData = async () => {
    // Load date values
    const accidentDateFromBackend =
      (await caseStore.getDataWithMapperKey(DatamapperKey.LAW_TRAFFIC_LAW_ACCIDENT_DATE)) || ''
    accidentDate.value = germanDateToIsoDate(accidentDateFromBackend)

    const expertDateFromBackend =
      (await caseStore.getDataWithMapperKey(DatamapperKey.LAW_TRAFFIC_LAW_EXPERT_DATE)) || ''
    expertDate.value = germanDateToIsoDate(expertDateFromBackend)

    // Load tax options
    isDifferenzsteuer.value =
      (await caseStore.getDataWithMapperKey(DatamapperKey.LAW_TRAFFIC_LAW_DIFFERENCIAL_TAX)) ===
      true
    isRegelsteuer.value =
      (await caseStore.getDataWithMapperKey(DatamapperKey.LAW_TRAFFIC_LAW_REGULAR_TAX)) === true

    // Load numeric values
    const repairCostsWithVatValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_COSTS_WITH_VAT
    )
    repairCostsWithVat.value = repairCostsWithVatValue ? parseFloat(repairCostsWithVatValue) : 0

    const depreciationValueValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_MERANTILE_VALUE_LOSS
    )
    merantileValueLoss.value = depreciationValueValue ? parseFloat(depreciationValueValue) : 0

    const replacementValueGrossValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_REPLACEMENT_VALUE
    )
    grossReplacementValue.value = replacementValueGrossValue
      ? parseFloat(replacementValueGrossValue)
      : 0

    const residualValueGrossValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_RESIDUAL_VALUE
    )
    grossResidualValue.value = residualValueGrossValue ? parseFloat(residualValueGrossValue) : 0

    const repairDurationValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_DURATION
    )
    repairDuration.value = repairDurationValue ? parseFloat(repairDurationValue) : 0
  }

  // Call loadInitialData when component is mounted
  loadInitialData()

  // Save functions for each field
  const saveAccidentDate = debounce(async (value: string) => {
    // Convert from ISO format to German format for backend
    const germanDate = isoDateToGermanDate(value)
    await caseStore.saveDataWithMapperKey(DatamapperKey.LAW_TRAFFIC_LAW_ACCIDENT_DATE, germanDate)
  }, 500)

  const saveExpertDate = debounce(async (value: string) => {
    // Convert from ISO format to German format for backend
    const germanDate = isoDateToGermanDate(value)
    await caseStore.saveDataWithMapperKey(DatamapperKey.LAW_TRAFFIC_LAW_EXPERT_DATE, germanDate)
  }, 500)

  const saveRepairCostsWithVat = debounce(async (value: number) => {
    await caseStore.saveDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_COSTS_WITH_VAT,
      value.toString()
    )
  }, 500)

  const saveMerantileValueLoss = debounce(async (value: number) => {
    await caseStore.saveDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_MERANTILE_VALUE_LOSS,
      value.toString()
    )
  }, 500)

  const saveGrossReplacementValue = debounce(async (value: number) => {
    await caseStore.saveDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_REPLACEMENT_VALUE,
      value.toString()
    )
    // Also save the net replacement value
    await caseStore.saveDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_NET_REPLACEMENT_VALUE,
      formatToTwoDecimals(netReplacementValue.value)
    )
  }, 500)

  const saveGrossResidualValue = debounce(async (value: number) => {
    await caseStore.saveDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_RESIDUAL_VALUE,
      value.toString()
    )
  }, 500)

  const saveDifferenzsteuer = debounce(async (value: boolean) => {
    await caseStore.saveDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_DIFFERENCIAL_TAX,
      value.toString()
    )
    // Also save the net replacement value
    await caseStore.saveDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_NET_REPLACEMENT_VALUE,
      formatToTwoDecimals(netReplacementValue.value)
    )
  }, 500)

  const saveRegelsteuer = debounce(async (value: boolean) => {
    await caseStore.saveDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_REGULAR_TAX,
      value.toString()
    )
    // Also save the net replacement value
    await caseStore.saveDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_NET_REPLACEMENT_VALUE,
      formatToTwoDecimals(netReplacementValue.value)
    )
  }, 500)

  const saveRepairDuration = debounce(async (value: number) => {
    await caseStore.saveDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_DURATION,
      value.toString()
    )
  }, 500)

  // Format function for read-only fields
  const formatToTwoDecimals = (value: number | string | null | undefined): string => {
    // Ensure value is a number before using toFixed
    const numValue = typeof value === 'number' ? value : Number(value || 0)
    return isNaN(numValue) ? '0.00' : numValue.toFixed(2)
  }

  // Format computed values for display in read-only fields
  const formattedRepairCostsWithoutVat = computed(() =>
    formatToTwoDecimals(repairCostsWithoutVat.value)
  )
  const formattedNetReplacementValue = computed(() =>
    formatToTwoDecimals(netReplacementValue.value)
  )
  const formattedNetGrossReplacementCosts = computed(() =>
    formatToTwoDecimals(netGrossReplacementCosts.value)
  )
  const formattedGrossGrossReplacementCosts = computed(() =>
    formatToTwoDecimals(grossGrossReplacementCosts.value)
  )

  // Calculate days between two dates
  const assessmentDays = computed(() => {
    if (!expertDate.value || !accidentDate.value) return 0

    // Create Date objects from the ISO format dates (yyyy-MM-dd)
    const expertDateObj = new Date(expertDate.value)
    const accidentDateObj = new Date(accidentDate.value)

    // Calculate the difference in milliseconds and convert to days
    const diffTime = expertDateObj.getTime() - accidentDateObj.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return diffDays
  })

  const damageAssessmentDays = computed(() => {
    return Math.max(0, assessmentDays.value)
  })

  // Calculate consideration days based on repair and replacement values
  const considerationDays = computed(() => {
    if (!grossReplacementValue.value) return 0

    return repairCostsWithVat.value + grossResidualValue.value <=
      grossReplacementValue.value * 1.3 &&
      repairCostsWithVat.value + grossResidualValue.value > netGrossReplacementCosts.value
      ? 5
      : 0
  })

  // Calculate total days
  const totalDays = computed(() => {
    return damageAssessmentDays.value + considerationDays.value
  })

  const totalDaysFromStore = computed(() => {
    return (
      Number(caseStore.getDataWithMapperKey(DatamapperKey.LAW_TRAFFIC_LAW_CALCULATED_TOTAL_DAYS)) ||
      0
    )
  })

  const isSavingTotalDays = ref(false)

  // Save functions for computed values
  const saveRepairCostsWithoutVat = debounce(async (value: number) => {
    await caseStore.saveDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_COSTS_WITHOUT_VAT,
      String(value.toFixed(2))
    )
  }, 500)

  const saveNetReplacementValueDirect = debounce(async (value: number) => {
    await caseStore.saveDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_NET_REPLACEMENT_VALUE,
      String(value.toFixed(2))
    )
  }, 500)

  const saveNetGrossReplacementCosts = debounce(async (value: number) => {
    await caseStore.saveDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_NET_GROSS_REPLACEMENT_VALUE,
      String(value.toFixed(2))
    )
  }, 500)

  const saveGrossGrossReplacementCosts = debounce(async (value: number) => {
    await caseStore.saveDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_GROSS_GROSS_REPLACEMENT_VALUE,
      String(value.toFixed(2))
    )
  }, 500)

  const saveTotalDays = debounce(async (value: number) => {
    isSavingTotalDays.value = true
    await caseStore.saveDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_CALCULATED_TOTAL_DAYS,
      String(value)
    )
    isSavingTotalDays.value = false
  }, 500)

  // Add watchers for all computed properties
  watch(repairCostsWithoutVat, saveRepairCostsWithoutVat)
  watch(netReplacementValue, saveNetReplacementValueDirect)
  watch(netGrossReplacementCosts, saveNetGrossReplacementCosts)
  watch(grossGrossReplacementCosts, saveGrossGrossReplacementCosts)
</script>

<template>
  <div class="mt-2 flex h-full flex-col overflow-hidden">
    <div class="max-h-[70vh] flex-1 space-y-4 overflow-y-auto">
      <div class="space-y-4">
        <div class="grid grid-cols-2 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">{{ t('dates') }}</h3>
          <InputField
            v-model="accidentDate"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_ACCIDENT_DATE"
            :label="t('accidentDate')"
            type="date"
            @update:model-value="saveAccidentDate"
          />
          <InputField
            v-model="expertDate"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_EXPERT_DATE"
            :label="t('expertDate')"
            type="date"
            @update:model-value="saveExpertDate"
          />
        </div>

        <div class="grid grid-cols-2 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">
            {{ t('repairCosts') }}
          </h3>
          <InputField
            v-model="repairCostsWithVat"
            asterisk
            suffix="€"
            :label="t('grossRepairCosts')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_COSTS_WITH_VAT"
            @update:model-value="saveRepairCostsWithVat"
          />
          <InputField
            v-model="formattedRepairCostsWithoutVat"
            suffix="€"
            :label="t('netRepairCosts')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_COSTS_WITHOUT_VAT"
            :disabled="true"
          />
        </div>

        <div class="grid grid-cols-2 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">
            {{ t('values') }}
          </h3>
          <InputField
            v-model="merantileValueLoss"
            asterisk
            suffix="€"
            :label="t('valueLoss')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_MERANTILE_VALUE_LOSS"
            @update:model-value="saveMerantileValueLoss"
          />
          <InputField
            v-model="grossReplacementValue"
            asterisk
            suffix="€"
            :label="t('grossReplacementValue')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_REPLACEMENT_VALUE"
            @update:model-value="saveGrossReplacementValue"
          />
          <InputField
            v-model="formattedNetReplacementValue"
            suffix="€"
            :label="t('netReplacementValue')"
            name="netReplacementValue"
            :disabled="true"
          />
          <InputField
            v-model="grossResidualValue"
            asterisk
            suffix="€"
            :label="t('grossResidualValue')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_RESIDUAL_VALUE"
            @update:model-value="saveGrossResidualValue"
          />
          <InputField
            v-model="formattedNetGrossReplacementCosts"
            suffix="€"
            :label="t('netGrossReplacementCosts')"
            name="netResidualValue"
            :disabled="true"
          />
          <InputField
            v-model="formattedGrossGrossReplacementCosts"
            suffix="€"
            :label="t('grossGrossReplacementCosts')"
            name="grossGrossReplacementCosts"
            :disabled="true"
          />
          <InputField
            v-model="repairDuration"
            asterisk
            :suffix="t('dayOrDays')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_DURATION"
            :label="t('repairDuration')"
            type="number"
            step="1"
            @update:model-value="saveRepairDuration"
          />
          <InputField
            v-model="damageAssessmentDays"
            :suffix="t('dayOrDays')"
            :name="t('damageAssessmentTime')"
            :label="t('damageAssessmentTime')"
            type="number"
            :disabled="true"
          />
          <InputField
            v-model="considerationDays"
            :suffix="t('dayOrDays')"
            :name="t('considerationPeriod')"
            :label="t('considerationPeriod')"
            type="number"
            :disabled="true"
          />
        </div>

        <div class="grid grid-cols-1 grid-rows-3 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">
            {{ t('tax') }}
          </h3>
          <CheckboxField
            v-model="isDifferenzsteuer"
            class="col-span-2 col-start-1 col-end-3"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_DIFFERENCIAL_TAX"
            :label="t('differentialTax')"
            @update:model-value="saveDifferenzsteuer"
          />
          <CheckboxField
            v-model="isRegelsteuer"
            class="col-span-2 col-start-1 col-end-3"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_REGULAR_TAX"
            :label="t('regularTax')"
            @update:model-value="saveRegelsteuer"
          />
        </div>
      </div>
    </div>

    <!-- Result -->
    <div class="mt-4">
      <h3 class="text-md font-normal text-gray-900">
        {{ t('compensationForLossOfUse') }}
      </h3>
      <div class="flex items-center gap-4 px-1 pb-2">
        <div class="flex-grow">
          <InputField
            v-model="totalDays"
            :suffix="t('dayOrDays')"
            :name="t('days')"
            type="number"
            :disabled="true"
          />
        </div>
        <Button
          type="button"
          :disabled="totalDays === totalDaysFromStore || isSavingTotalDays"
          :class="[
            'inline-flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-white',
            'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2',
            isSavingTotalDays
              ? 'cursor-not-allowed bg-purple-400'
              : 'bg-purple-600 hover:bg-purple-700',
            'disabled:cursor-not-allowed disabled:opacity-50'
          ]"
          @click="() => saveTotalDays(totalDays)"
        >
          <svg
            v-if="isSavingTotalDays"
            class="h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Speichern</span>
        </Button>
      </div>
    </div>
  </div>
</template>
